import { useEffect, useState } from "react";
import { CalendarClock, AlertTriangle } from "lucide-react";
import { DatePicker } from "@/components/ui/date-picker";
import { finAPI } from "./finApi";

const inr = (m) => `₹${((m || 0) / 100).toLocaleString("en-IN")}`;

/** FIX 7 · "Catch up missed days": closes every open business date in order and reports missed checkout nights. */
export function NightAuditCatchUp({ stale, onDone }) {
  const [from, setFrom] = useState(stale?.[0]?.business_day || "");
  const [to, setTo] = useState(stale?.[stale.length - 1]?.business_day || "");
  const [postMissed, setPostMissed] = useState(false);
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);
  useEffect(() => {
    if (stale?.length) { setFrom((v) => v || stale[0].business_day); setTo((v) => v || stale[stale.length - 1].business_day); }
  }, [stale]);

  const run = async () => {
    if (!from || !to) { setError("Choose both dates"); return; }
    if (!window.confirm(`Close every business date from ${from} to ${to}? Room nights are posted once per stay per night; already-closed days are skipped.`)) return;
    setBusy(true); setError(null);
    try {
      const r = await finAPI.runNightAudit({ from, to, post_missed_checkouts: postMissed });
      setResult(r); onDone?.();
    } catch (e) {
      const d = e?.response?.data; setError(d?.message || d?.detail?.message || d?.detail || "Catch-up failed");
    } finally { setBusy(false); }
  };

  const missed = (result?.days || []).flatMap((d) => d.missed_checkout_nights || []);
  return (
    <div className="bg-white border border-slate-200 rounded-2xl p-5 space-y-3" data-testid="night-audit-catchup">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h3 className="font-display text-[14px] font-semibold text-hocp-navy inline-flex items-center gap-2"><CalendarClock className="w-4 h-4" /> Catch up missed days</h3>
          <p className="text-[12.5px] text-slate-500 mt-0.5">Runs each open business date in order, posts every room night once, closes the day and rolls the business date forward.</p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <div className="w-44"><DatePicker value={from} onChange={setFrom} data-testid="catchup-from" /></div>
          <span className="text-slate-400 text-[12px]">to</span>
          <div className="w-44"><DatePicker value={to} onChange={setTo} data-testid="catchup-to" /></div>
          <label className="inline-flex items-center gap-1.5 text-[12px] text-slate-600">
            <input type="checkbox" checked={postMissed} onChange={(e) => setPostMissed(e.target.checked)} data-testid="catchup-post-missed" />
            Post missed checkout nights
          </label>
          <button onClick={run} disabled={busy} data-testid="catchup-run"
            className="px-4 py-2 rounded-lg bg-hocp-navy text-white text-[13px] font-semibold disabled:opacity-60">{busy ? "Closing…" : "Run catch-up"}</button>
        </div>
      </div>
      {error && <div className="text-[12.5px] text-red-600" data-testid="catchup-error">{String(error)}</div>}
      {result && (
        <div className="space-y-2" data-testid="catchup-result">
          <div className="text-[13px] text-slate-700">
            <b>{result.closed}/{result.dates}</b> dates closed · <span className="text-emerald-700">{result.posted} nights posted</span> · {result.already_posted} already posted
            {missed.length > 0 && <> · <span className="text-amber-700">{missed.length} missed checkout night(s)</span></>}
          </div>
          <table className="w-full text-[12.5px]">
            <thead className="text-slate-500 text-left"><tr><th className="py-1">Date</th><th>Status</th><th>In-house</th><th>Posted</th><th>Replayed</th><th>Revenue</th><th>Missed</th></tr></thead>
            <tbody>
              {result.days.map((d) => (
                <tr key={d.business_date} className="border-t border-slate-100" data-testid={`catchup-day-${d.business_date}`}>
                  <td className="py-1 font-mono">{d.business_date}</td>
                  <td><span className={`px-2 py-0.5 rounded-full text-[11px] font-semibold ${d.status === "closed" || d.status === "already_closed" ? "bg-emerald-50 text-emerald-700" : "bg-red-50 text-red-700"}`}>{d.status.replace("_", " ")}</span></td>
                  <td>{d.in_house_stays ?? "—"}</td><td>{d.posted}</td><td>{d.already_posted}</td><td>{inr(d.posted_gross_minor)}</td><td>{(d.missed_checkout_nights || []).length}</td>
                </tr>
              ))}
            </tbody>
          </table>
          {missed.length > 0 && (
            <div className="rounded-xl border border-amber-200 bg-amber-50 p-3 text-[12.5px] text-amber-900" data-testid="catchup-missed-report">
              <div className="font-semibold inline-flex items-center gap-1.5"><AlertTriangle className="w-3.5 h-3.5" /> Checked-out stays with unposted nights {postMissed ? "(posted as back-dated adjustments)" : "(needs owner approval — re-run with “Post missed checkout nights”)"}</div>
              <ul className="mt-1 space-y-0.5">
                {missed.map((m) => <li key={`${m.stay_id}-${m.business_date}`}>{m.business_date} · {m.guest_name || m.stay_code} · folio {m.folio_status || "—"} · {m.status.replace("_", " ")}</li>)}
              </ul>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
